import React, { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@mui/material";
import { apiService } from "../../api/services/apiService";
import type { GatewaySku } from "../../types";
import { GatewaySkuStatus, GatewayType } from "../../types";
import { GATEWAY_ENV_OPTIONS } from "../../utils/gatewaySkuEnv";
import { GATEWAY_TYPE_LABELS, GATEWAY_TYPE_OPTIONS } from "../../utils/gatewayType";

interface EditGatewaySkuDialogProps {
  gatewaySku: GatewaySku | null;
  open: boolean;
  onClose: () => void;
  onSaved: (updated: GatewaySku) => void;
}

const EditGatewaySkuDialog: React.FC<EditGatewaySkuDialogProps> = ({
  gatewaySku,
  open,
  onClose,
  onSaved,
}) => {
  const [draft, setDraft] = useState<GatewaySku | null>(gatewaySku);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(gatewaySku);
  }, [gatewaySku]);

  const handleSave = async () => {
    if (!draft?.uuid) return;
    if (
      !draft.gatewayProductIdentifier?.trim() ||
      !draft.gatewayProductName?.trim() ||
      !draft.gatewayCommonName?.trim() ||
      !draft.gatewayId?.trim() ||
      !draft.envName?.trim()
    ) {
      alert("Gateway identifier, names, gateway ID, and environment are required.");
      return;
    }
    setSaving(true);
    try {
      const updated = await apiService.updateGatewaySku(draft.uuid, {
        gatewayProductIdentifier: draft.gatewayProductIdentifier.trim(),
        gatewayProductName: draft.gatewayProductName.trim(),
        gatewayCommonName: draft.gatewayCommonName.trim(),
        gatewayId: draft.gatewayId.trim(),
        gatewayType: draft.gatewayType,
        envName: draft.envName.trim(),
        skuStatus: draft.skuStatus,
        description: draft.description ?? undefined,
      });
      onSaved(updated);
      onClose();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update gateway SKU");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Gateway SKU</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <TextField
          fullWidth
          label="Gateway product identifier *"
          margin="normal"
          value={draft?.gatewayProductIdentifier ?? ""}
          onChange={(e) =>
            setDraft((prev) =>
              prev ? { ...prev, gatewayProductIdentifier: e.target.value } : prev,
            )
          }
        />
        <TextField
          fullWidth
          label="Gateway product name *"
          margin="normal"
          value={draft?.gatewayProductName ?? ""}
          onChange={(e) =>
            setDraft((prev) =>
              prev ? { ...prev, gatewayProductName: e.target.value } : prev,
            )
          }
        />
        <TextField
          fullWidth
          label="Gateway common name *"
          margin="normal"
          value={draft?.gatewayCommonName ?? ""}
          onChange={(e) =>
            setDraft((prev) =>
              prev ? { ...prev, gatewayCommonName: e.target.value } : prev,
            )
          }
        />
        <TextField
          fullWidth
          label="Gateway ID *"
          margin="normal"
          value={draft?.gatewayId ?? ""}
          onChange={(e) =>
            setDraft((prev) => (prev ? { ...prev, gatewayId: e.target.value } : prev))
          }
        />
        <TextField
          fullWidth
          select
          label="Gateway type *"
          margin="normal"
          value={draft?.gatewayType ?? GatewayType.APIGEE_EDGE}
          onChange={(e) =>
            setDraft((prev) =>
              prev ? { ...prev, gatewayType: e.target.value as GatewayType } : prev,
            )
          }
        >
          {GATEWAY_TYPE_OPTIONS.map((type) => (
            <MenuItem key={type} value={type}>
              {GATEWAY_TYPE_LABELS[type]}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          select
          label="Environment *"
          margin="normal"
          value={draft?.envName ?? "dev"}
          onChange={(e) =>
            setDraft((prev) => (prev ? { ...prev, envName: e.target.value } : prev))
          }
        >
          {GATEWAY_ENV_OPTIONS.map((env) => (
            <MenuItem key={env} value={env}>
              {env}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          select
          label="SKU status"
          margin="normal"
          value={draft?.skuStatus ?? GatewaySkuStatus.ACTIVE}
          onChange={(e) =>
            setDraft((prev) =>
              prev ? { ...prev, skuStatus: e.target.value as GatewaySkuStatus } : prev,
            )
          }
        >
          {Object.values(GatewaySkuStatus).map((status) => (
            <MenuItem key={status} value={status}>
              {status}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          fullWidth
          label="Description"
          margin="normal"
          multiline
          minRows={2}
          value={draft?.description ?? ""}
          onChange={(e) =>
            setDraft((prev) => (prev ? { ...prev, description: e.target.value } : prev))
          }
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditGatewaySkuDialog;
